import React, { forwardRef, useImperativeHandle, useRef } from 'react'

import { Chart as ChartJs } from 'chart.js'
import { Chart } from 'react-chartjs-2'
import { getDataBy, ChartPlugins, getOptionsBy } from './utils'
import { BabyGrowthPercentiles } from '~/interfaces'

declare module 'chart.js' {
  interface PluginOptionsByType<TType extends ChartType> {
    drawScalesTitles?: {
      xTitle: string
      yTitle: string
    }
  }
}

export interface IAxisInformation<T> {
  x: T
  y: T
}

interface BabyGrowthChartProps {
  percentiles: BabyGrowthPercentiles
  axisLabels: IAxisInformation<string>
  annotations: Array<IAxisInformation<number>>
}

export interface BabyGrowthChartImperativeMethods {
  handleToggleAnnotations: () => void
}

export const BabyGrowthChart = forwardRef<BabyGrowthChartImperativeMethods, BabyGrowthChartProps>(
  (props, ref): JSX.Element => {
    const { percentiles, axisLabels, annotations } = props
    const chartRef = useRef<ChartJs<'line'>>(null)

    const handleToggleAnnotations = () => {
      if (chartRef?.current) {
        const scatterDatasetPosition = 5
        const isScatterDatasetVisible = chartRef.current.isDatasetVisible(scatterDatasetPosition)

        if (isScatterDatasetVisible) {
          chartRef.current.hide(scatterDatasetPosition)
        }
        if (!isScatterDatasetVisible) {
          chartRef.current.show(scatterDatasetPosition)
        }
      }
    }

    useImperativeHandle(ref, () => ({ handleToggleAnnotations }))

    return (
      <Chart
        type="line"
        ref={chartRef}
        data={getDataBy(percentiles, annotations)}
        options={getOptionsBy(axisLabels, percentiles, annotations)}
        plugins={[...Object.values(ChartPlugins)]}
      />
    )
  }
)

BabyGrowthChart.displayName = 'BabyGrowthChart'
